/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Toolbar,
  TextField,
  Button,
  AlertColor,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { tfidHighlightExtension } from "utils/TfidCodemirrorExtension";
import ResizableForm from "./ResizableForm";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import RefreshIcon from "@mui/icons-material/Refresh";

type Order = "asc" | "desc";

export interface Column<T> {
  key: keyof T;
  label: string;
  type: "string" | "object";
  default?: string;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  disableAdd?: boolean;
  disableEdit?: boolean;
  enabledSort?: "asc" | "desc" | null;
}

interface GenericTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  onSearch?: (query: string) => void;
  enablePagination?: boolean;
  enableSorting?: boolean;
  onAddRow?: (newRow: T) => void;
  onEditRow?: (updatedRow: T) => void;
  onDeleteRow?: (rowId: string) => void;
  onRefresh?: () => void;
  renderCustomInput?: (key: keyof T, value: any, handleInputChange: (key: keyof T, value: any) => void) => React.ReactNode;
}

const truncateContent = (content: string, maxLength = 100): string => {
  return content.length > maxLength ? `${content.substring(0, maxLength)}...` : content;
};

/**
 * GenericTable Component
 *
 * A reusable table component that supports features like sorting, pagination, CRUD operations, and custom input rendering.
 *
 * @template T - The type of the data rows. Each row must have a unique `id` property.
 *
 * Props:
 * - `rows: T[]` - The data to be displayed in the table. Each row must have a unique `id`.
 * - `columns: Column<T>[]` - Defines the structure of the table, including column keys, labels, types, and optional render functions.
 * - `onSearch?: (query: string) => void` - Callback triggered when the search input changes. Useful for filtering rows externally.
 * - `enablePagination?: boolean` - Enables or disables pagination. Defaults to `true`.
 * - `enableSorting?: boolean` - Enables or disables column sorting. Defaults to `true`.
 * - `onAddRow?: (newRow: T) => void` - Callback triggered when a new row is added.
 * - `onEditRow?: (updatedRow: T) => void` - Callback triggered when a row is edited.
 * - `onDeleteRow?: (rowId: string) => void` - Callback triggered when a row is deleted.
 * - `onRefresh?: () => void` - Callback triggered when refresh button is clicked.
 * - `renderCustomInput?: (key: keyof T, value: any, handleInputChange: (key: keyof T, value: any) => void) => React.ReactNode` -
 *   Function to render custom input fields for specific columns during row editing or adding.
 * - `disableCrudRow?: boolean` - Disables all CRUD operations (Add, Edit, Delete) when set to `true`. Defaults to `false`.
 *
 * Features:
 * - Sorting: Clickable column headers for ascending/descending sorting.
 * - Pagination: Displays rows in pages with configurable rows per page.
 * - Search: Filters rows based on a search query.
 * - CRUD Operations: Add, edit, and delete rows with optional callbacks.
 * - Custom Input Rendering: Allows custom input components for specific columns.
 * - Expandable Rows: Supports inline editing of rows when expanded.
 *
 * Usage:
 * - Import the `GenericTable` component and provide the required `rows` and `columns` props.
 * - Optionally, implement callbacks for CRUD operations and custom input rendering.
 */
export const GenericTable = <T extends { id: string }>({
  rows,
  columns,
  onSearch,
  enablePagination = true,
  enableSorting = true,
  onAddRow,
  onEditRow,
  onDeleteRow,
  onRefresh,
  renderCustomInput,
}: GenericTableProps<T>) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [editedRow, setEditedRow] = useState<Partial<T> | null>(null);
  const [newRow, setNewRow] = useState<T>({} as T);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Alerts
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  // Initialize order and orderBy states based on enabledSort
  const defaultSortColumns = columns.filter((col) => col.enabledSort);

  const [sortConfig, setSortConfig] = useState<{ key: keyof T; order: Order }[]>(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    defaultSortColumns.map((col) => ({ key: col.key, order: col.enabledSort! })),
  );

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const handleSort = (property: keyof T) => {
    setSortConfig((prevConfig) => {
      const existing = prevConfig.find((config) => config.key === property);
      if (existing) {
        return prevConfig.map((config) =>
          config.key === property ? { ...config, order: config.order === "asc" ? "desc" : "asc" } : config,
        );
      }
      return [...prevConfig, { key: property, order: "asc" }];
    });
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    if (onSearch) onSearch(query);
  };

  const handleExpandRow = (row: T) => {
    if (expandedRowId === row.id) {
      setExpandedRowId(null);
      setEditedRow(null);
    } else {
      setExpandedRowId(row.id);
      setEditedRow(row);
    }
  };

  const handleEditInputChange = (key: keyof T, value: string | object) => {
    if (editedRow) {
      setEditedRow({ ...editedRow, [key]: value });
    }
  };

  const handleNewInputChange = (key: keyof T, value: string | object) => {
    setNewRow({ ...newRow, [key]: value });
  };

  const handleSave = async () => {
    if (editedRow && onEditRow) {
      try {
        const filteredEditedRow = Object.fromEntries(
          Object.entries(editedRow).filter(([key]) => !columns.find((col) => col.key === key)?.disableEdit),
        ) as T;
        await onEditRow(filteredEditedRow);
        setExpandedRowId(null);
        setEditedRow(null);
      } catch (e: any) {
        setAlertMessage(e?.errorArgs || e?.debugMessage || "An error occurred while editing row.");
        setAlertSeverity("error");
      }
    }
  };

  const handleAddNew = async () => {
    if (onAddRow) {
      try {
        const filteredNewRow = Object.fromEntries(
          Object.entries(newRow).filter(([key]) => !columns.find((col) => col.key === key)?.disableAdd),
        ) as T;
        await onAddRow({ ...filteredNewRow, id: String(rows.length + 1) } as T);
        setIsNewDialogOpen(false);
        setNewRow({} as T);
      } catch (e: any) {
        setAlertMessage(e?.errorArgs || e?.debugMessage || "An error occurred while adding the new row.");
        setAlertSeverity("error");
      }
    }
  };

  const handleDeleteClick = (row: T) => {
    if (!row || !row.id) {
      console.error("Invalid row object:", row);
      return;
    }
    setRowToDelete(row.id);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (rowToDelete && onDeleteRow) {
      try {
        await onDeleteRow(rowToDelete);
      } catch (e: any) {
        setAlertMessage(e?.debugMessage || "An error occurred while deleting row.");
        setAlertSeverity("error");
      }
    }
    setIsDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const sortedRows = React.useMemo(() => {
    if (!enableSorting || sortConfig.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const { key, order } of sortConfig) {
        if (a[key] < b[key]) return order === "asc" ? -1 : 1;
        if (a[key] > b[key]) return order === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [rows, sortConfig, enableSorting]);

  const filteredRows = React.useMemo(() => {
    return sortedRows.filter((row) =>
      columns.some((column) => {
        const rawValue = row[column.key];
        const renderedValue = column.render ? column.render(rawValue, row) : rawValue;
        const valueToSearch = typeof renderedValue === "string" ? renderedValue : String(rawValue);
        return valueToSearch.toLowerCase().includes(searchQuery);
      }),
    );
  }, [sortedRows, searchQuery, columns]);

  const paginatedRows = React.useMemo(() => {
    if (!enablePagination) return filteredRows;
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, page, rowsPerPage, enablePagination]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, display: "flex", alignItems: "center", gap: "8px" }}>
          {onAddRow && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsNewDialogOpen(true)}
              sx={{ height: "40px" }} // Set consistent height
            >
              New
            </Button>
          )}
          <TextField
            id="outlined-search"
            label="Search"
            type="search"
            size="small"
            onChange={handleSearchChange}
            sx={{ width: "360px", height: "40px" }} // Set consistent height
          />
          {onRefresh && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{ height: "40px" }} // Set consistent height
            >
              Refresh
            </Button>
          )}
        </Toolbar>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={String(column.key)}>
                    {enableSorting ? (
                      <TableSortLabel
                        active={!!sortConfig.find((config) => config.key === column.key)}
                        direction={sortConfig.find((config) => config.key === column.key)?.order || "asc"}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                {onDeleteRow && <TableCell>Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    onClick={() => handleExpandRow(row)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: expandedRowId === row.id ? "#f5f5f5" : "inherit",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    {columns.map((column) => {
                      const key = column.key;
                      const value = typeof row[key] === "object" ? JSON.stringify(row[key]) : String(row[key]);
                      return (
                        <TableCell key={String(key)}>
                          {column.render ? column.render(row[column.key], row) : truncateContent(String(value))}
                        </TableCell>
                      );
                    })}
                    {onDeleteRow && (
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(row);
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>

                  {onEditRow && expandedRowId === row.id && !isDeleteDialogOpen && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {columns
                            .filter((col) => !col.disableEdit)
                            .map((column) => {
                              const value = (editedRow as any)?.[column.key] ?? row[column.key] ?? column.default;
                              (editedRow as any)[column.key] = value;
                              return (
                                <div key={String(column.key)}>
                                  {renderCustomInput &&
                                  renderCustomInput(column.key, (editedRow as any)[column.key], handleEditInputChange) ? (
                                    renderCustomInput(column.key, (editedRow as any)[column.key], handleEditInputChange)
                                  ) : column.type === "string" ? (
                                    <TextField
                                      label={column.label}
                                      value={value}
                                      onChange={(e) => handleEditInputChange(column.key, e.target.value)}
                                      fullWidth
                                    />
                                  ) : column.type === "object" ? (
                                    <div>
                                      <label>{column.label}</label>
                                      <CodeMirror
                                        value={typeof value === "string" ? value : JSON.stringify(value, null, 2)}
                                        height="100%"
                                        style={{ height: "80%", width: "100%" }}
                                        extensions={[json(), tfidHighlightExtension]}
                                        onChange={(val) => handleEditInputChange(column.key, JSON.parse(val))}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <Button onClick={() => setExpandedRowId(null)}>Cancel</Button>
                            <Button onClick={handleSave} variant="contained" color="primary">
                              Save
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {enablePagination && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </Paper>
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this row? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <ResizableForm
        isOpen={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
        onSubmit={handleAddNew}
        columns={columns.filter((col) => !col.disableAdd)}
        newRow={newRow}
        handleInputChange={handleNewInputChange}
        renderCustomInput={renderCustomInput}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
